import type React from 'react';
import type { CodFormCorners, CodFormStyle } from '@xshop/shared';

// The merchant's form look (Settings → Checkout → Form style) as what the form's root carries: CSS variables for the
// order button's colours and both corners, and a class for filled fields. Anything left empty keeps the theme's own.

/** How round each corner style draws the button and the fields; THEME sets nothing. */
const RADIUS: Record<Exclude<CodFormCorners, 'THEME'>, string> = { SQUARE: '0px', ROUNDED: '10px', PILL: '999px' };

// Reads the variables and classes one form wears for a look, nothing at all for the theme's.
export function codFormLook(style: Partial<CodFormStyle> | undefined): { className: string; vars: React.CSSProperties } {
  const vars: Record<string, string> = {};
  if (style?.buttonColor) vars['--cta-fill'] = style.buttonColor;
  if (style?.buttonTextColor) vars['--cta-ink'] = style.buttonTextColor;
  if (style?.corners && style.corners !== 'THEME') {
    vars['--xe-cod-button-radius'] = RADIUS[style.corners];
    vars['--xe-cod-field-radius'] = RADIUS[style.corners];
  }
  const classes = [style?.fieldLook === 'FILLED' ? 'cod-fields-filled' : '', style?.corners === 'PILL' ? 'cod-corners-pill' : ''];
  return { className: classes.filter(Boolean).join(' '), vars: vars as React.CSSProperties };
}

// Words the order button: the merchant's text, else the theme's or the language's, with the total when they want it.
export function orderButtonText(style: Partial<CodFormStyle> | undefined, fallback: string, total: string | null): string {
  const words = style?.buttonText?.trim() || fallback;
  return style?.showTotalInButton === false || total === null ? words : `${words} (${total})`;
}

// Words the note under the button, or null when the merchant hid it.
export const secureNoteText = (style: Partial<CodFormStyle> | undefined, fallback: string): string | null =>
  (style?.showSecureNote === false ? null : style?.secureNote?.trim() || fallback);
